import * as React from "react";
import { ExternalLink, FileText, History, Mail, Network, Pencil, Phone, Unlink, UserRound } from "lucide-react";
import { Link } from "react-router";
import { toast } from "react-toastify";

import { useDetachSupplierFamilyMutation, useUpdateSupplierFamilyMetadataMutation } from "@/api/suppliers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useContractFamiliesAccess } from "@/hooks/use-contract-families-access";
import { cn } from "@/lib/utils";
import type {
  ContractFamilyMetadataRequest,
  ProcurementStatus,
  SupplierFamilySummary,
  SupplierFamilySummaryField,
  SupplierStatus,
} from "@/types/supplier";
import type { DirectoryUser } from "@/types/user";
import { ContractSummaryRegenerateDialog } from "@/views/drive/modals/contractSummaryRegenerateDialog";

import { ContractValue, GbpEquivalent, SummaryDate, SummaryText } from "./contract-family-enrichment";
import { ContractFamilyMetadataForm } from "./contract-family-metadata-form";
import { SupplierFamilyAuditLogDialog } from "./supplier-family-audit-log";
import { SupplierFamilyNotes } from "./supplier-family-notes";
import {
  CONTRACT_SUMMARY_ACTION_LABELS,
  contractSummaryAction,
  contractSummaryStatusBadge,
} from "./contract-summary-status";

interface Props {
  supplierId: string;
  initialFamilyId?: string;
  supplierOwnerId: string | null;
  supplierStatus: SupplierStatus;
  families: SupplierFamilySummary[];
  ownersById: ReadonlyMap<string, DirectoryUser>;
  onChanged: () => void;
}

const familyTabStatusClasses: Record<ProcurementStatus | "not-set", string> = {
  active:
    "border-[#b9dfcc] bg-[#e6f4ed] text-[#245f40] hover:bg-[#d9eee4] data-[state=active]:border-[#79b995] [&_svg]:text-[#2d7a4f]",
  expiring:
    "border-[#efd39f] bg-[#fdf0db] text-[#805515] hover:bg-[#f9e5c4] data-[state=active]:border-[#d6a34f] [&_svg]:text-[#a06b1a]",
  expired:
    "border-[#efc2bd] bg-[#fde8e6] text-[#9e3025] hover:bg-[#f8d9d6] data-[state=active]:border-[#d77f75] [&_svg]:text-[#c0392b]",
  archived:
    "border-[#efc2bd] bg-[#fde8e6] text-[#9e3025] hover:bg-[#f8d9d6] data-[state=active]:border-[#d77f75] [&_svg]:text-[#c0392b]",
  draft:
    "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 data-[state=active]:border-slate-300 [&_svg]:text-slate-500",
  "not-set":
    "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 data-[state=active]:border-slate-300 [&_svg]:text-slate-500",
};

const familyTabStatusLabel = (status: ProcurementStatus | null): string => {
  if (!status) return "Not set";
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
};

const familyStatusOrder: Record<ProcurementStatus, number> = {
  draft: 0,
  active: 1,
  expiring: 2,
  expired: 3,
  archived: 4,
};
const unsetFamilyStatusOrder = Object.keys(familyStatusOrder).length;

const getFamilyStatusOrder = (status: ProcurementStatus | null) =>
  status === null ? unsetFamilyStatusOrder : (familyStatusOrder[status] ?? unsetFamilyStatusOrder);

const fromSummary = (family: SupplierFamilySummary, field: SupplierFamilySummaryField) =>
  family.summary_fields?.includes(field) ?? false;

const SummarySource = ({ show }: { show: boolean }) =>
  show ? <span className="ml-1.5 text-[11px] font-medium text-desert-sand-700">From contract summary</span> : null;

const PERFORMANCE_FIELDS = [
  ["performance_regime", "Regime"],
  ["performance_sla_kpis", "SLA / KPIs"],
  ["performance_failure_consequences", "Failure consequences"],
] as const;

const COMMERCIAL_TERM_FIELDS = [
  ["payment_terms", "Payment terms"],
  ["obligations", "Obligations"],
] as const;

interface SummarySectionProps {
  family: SupplierFamilySummary;
  title: string;
  // Fields stack; short values may sit in a grid instead.
  layout?: "stack" | "grid";
  children: React.ReactNode;
}

// Short values share one tinted grid; long model text gets a card per field so
// adjacent fields never read as one block.
const VALUE_GROUP_CLASSES =
  "grid gap-x-6 gap-y-3 rounded-md border border-[#ebe5de] bg-[#f7f2ec]/50 p-4 sm:grid-cols-2 lg:grid-cols-4";
const FIELD_CARD_CLASSES = "rounded-md border border-[#ebe5de] bg-white px-4 py-3";

const SummaryLayoutContext = React.createContext<NonNullable<SummarySectionProps["layout"]>>("stack");

// One labelled block of contract summary fields; fields stack so long model text stays readable.
const SummarySection = ({ family, title, layout = "stack", children }: SummarySectionProps) => (
  <section aria-label={`${family.name} ${title.toLowerCase()}`} className="mt-5">
    <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{title}</h4>
    <dl className={cn("mt-2 text-sm text-gray-700", layout === "grid" ? VALUE_GROUP_CLASSES : "space-y-2")}>
      <SummaryLayoutContext value={layout}>{children}</SummaryLayoutContext>
    </dl>
  </section>
);

interface SummaryFieldProps {
  label: string;
  // A field that repeats its section heading keeps the term for screen readers only.
  hideLabel?: boolean;
  children: React.ReactNode;
}

// Stacked fields are cards; grid fields are plain cells inside the section's value group.
const SummaryField = ({ label, hideLabel = false, children }: SummaryFieldProps) => {
  const isCard = React.use(SummaryLayoutContext) === "stack";
  return (
    <div className={isCard ? FIELD_CARD_CLASSES : undefined}>
      <dt className={hideLabel ? "sr-only" : "text-xs font-medium text-gray-500"}>{label}</dt>
      <dd className={hideLabel ? undefined : isCard ? "mt-1" : "mt-0.5"}>{children}</dd>
    </div>
  );
};

const PriceEscalation = ({ family }: { family: SupplierFamilySummary }) => {
  if (!family.price_escalation && !family.next_price_escalation_date) return null;
  return (
    <SummarySection family={family} title="Price escalation">
      {family.price_escalation ? (
        <SummaryField label="Price escalation" hideLabel>
          <SummaryText text={family.price_escalation} />
        </SummaryField>
      ) : null}
      {family.next_price_escalation_date ? (
        <SummaryField label="Next price escalation">
          <SummaryDate date={family.next_price_escalation_date} />
        </SummaryField>
      ) : null}
    </SummarySection>
  );
};

const SummaryFields = ({
  family,
  title,
  fields,
}: {
  family: SupplierFamilySummary;
  title: string;
  fields: typeof COMMERCIAL_TERM_FIELDS | typeof PERFORMANCE_FIELDS;
}) => {
  const entries = fields.filter(([field]) => family[field]);
  if (entries.length === 0) return null;
  return (
    <SummarySection family={family} title={title}>
      {entries.map(([field, label]) => (
        <SummaryField key={field} label={label}>
          <SummaryText text={family[field] ?? ""} />
        </SummaryField>
      ))}
    </SummarySection>
  );
};

const CommercialTerms = ({ family }: { family: SupplierFamilySummary }) => (
  <SummaryFields family={family} title="Commercial terms" fields={COMMERCIAL_TERM_FIELDS} />
);

const SupplierPerformance = ({ family }: { family: SupplierFamilySummary }) => (
  <SummaryFields family={family} title="Supplier performance" fields={PERFORMANCE_FIELDS} />
);

// Values extracted from the contract summary, kept apart from the metadata the user enters.
// They help fill in metadata and never replace it. A hedged answer ("25,000 USD minimum") has
// no typed value and is shown as the summary states it.
const AiGeneratedValues = ({ family }: { family: SupplierFamilySummary }) => (
  <SummarySection family={family} title="AI Generated Values" layout="grid">
    <SummaryField label="Status">{family.system_status ?? "Not set"}</SummaryField>
    <SummaryField label="Total value">
      <ContractValue
        value={family.summary_total_contract_value}
        currencyCode={family.summary_currency_code}
        fallback={family.summary_total_contract_value_text}
      />
    </SummaryField>
    <SummaryField label="Annual value">
      <ContractValue
        value={family.summary_annual_contract_value}
        currencyCode={family.summary_currency_code}
        fallback={family.summary_annual_contract_value_text}
      />
    </SummaryField>
    <SummaryField label="Expiry date">
      {family.summary_expiry_date ? (
        <SummaryDate date={family.summary_expiry_date} />
      ) : (
        (family.summary_expiry_date_text ?? "Not set")
      )}
    </SummaryField>
  </SummarySection>
);

const FamilyOwnerDetails = ({ owner }: { owner?: DirectoryUser }) => {
  if (!owner) return <span>Owner unavailable</span>;
  const name = [owner.name, owner.surname].filter(Boolean).join(" ") || owner.email;

  return (
    <>
      <span className="flex items-center gap-2 font-medium text-gray-700">
        <UserRound className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {name}
      </span>
      <span className="flex items-center gap-2">
        <Mail className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {owner.email}
      </span>
    </>
  );
};

export const SupplierFamilyPanel = ({
  supplierId,
  initialFamilyId,
  supplierOwnerId,
  supplierStatus,
  families,
  ownersById,
  onChanged,
}: Props) => {
  const orderedFamilies = React.useMemo(
    () =>
      [...families].sort(
        (left, right) => getFamilyStatusOrder(left.procurement_status) - getFamilyStatusOrder(right.procurement_status),
      ),
    [families],
  );
  const [selectedFamilyId, setSelectedFamilyId] = React.useState(() =>
    initialFamilyId && orderedFamilies.some(family => family.id === initialFamilyId)
      ? initialFamilyId
      : (orderedFamilies[0]?.id ?? ""),
  );
  const [editing, setEditing] = React.useState<SupplierFamilySummary | null>(null);
  const [detaching, setDetaching] = React.useState<SupplierFamilySummary | null>(null);
  const [summarising, setSummarising] = React.useState<SupplierFamilySummary | null>(null);
  const [auditing, setAuditing] = React.useState<SupplierFamilySummary | null>(null);
  const { hasContractFamilies } = useContractFamiliesAccess();
  const [message, setMessage] = React.useState<string | null>(null);
  const [ownerError, setOwnerError] = React.useState<string | null>(null);
  const [detach, { isLoading: isDetaching }] = useDetachSupplierFamilyMutation();
  const [updateMetadata, { isLoading: isUpdating }] = useUpdateSupplierFamilyMetadataMutation();
  const selectedFamily = orderedFamilies.find(family => family.id === selectedFamilyId) ?? orderedFamilies[0];

  React.useEffect(() => {
    if (!orderedFamilies.some(family => family.id === selectedFamilyId)) {
      setSelectedFamilyId(orderedFamilies[0]?.id ?? "");
    }
  }, [orderedFamilies, selectedFamilyId]);

  const openEdit = (family: SupplierFamilySummary) => {
    setEditing(family);
    setMessage(null);
    setOwnerError(null);
  };

  const saveMetadata = async (data: ContractFamilyMetadataRequest) => {
    if (!editing) return;
    setOwnerError(null);
    try {
      await updateMetadata({
        supplierId,
        familyId: editing.id,
        data,
      }).unwrap();
      toast.success("Contract Family metadata updated");
      setEditing(null);
      onChanged();
    } catch (error) {
      const code = (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code;
      if (code === "contract_family_owner_required") {
        setOwnerError("Select an active Contract Family owner");
      } else {
        setMessage(
          code === "contract_family_external_id_conflict"
            ? "This External ID is already in use."
            : "Contract Family metadata could not be updated.",
        );
      }
    }
  };

  const confirmDetach = async () => {
    if (!detaching) return;
    try {
      await detach({ supplierId, familyId: detaching.id }).unwrap();
      toast.success("Contract Family detached");
      setDetaching(null);
      onChanged();
    } catch {
      setMessage("The relationship changed. Refresh the page and try again.");
    }
  };

  if (families.length === 0)
    return (
      <div className="px-6 py-12 text-center text-sm font-medium text-gray-500">
        No accessible Contract Families are assigned.
      </div>
    );

  return (
    <>
      <Tabs value={selectedFamily?.id} onValueChange={setSelectedFamilyId} className="w-full">
        <div className="overflow-x-auto border-b border-[#ebe5de] bg-[#f7f2ec] px-4 pt-3">
          <TabsList
            aria-label="Assigned Contract Families"
            className="h-auto min-w-max justify-start gap-1 rounded-none bg-transparent p-0"
          >
            {orderedFamilies.map(family => {
              const statusDescriptionId = `contract-family-${family.id}-status`;
              return (
                <TabsTrigger
                  key={family.id}
                  value={family.id}
                  aria-label={family.name}
                  aria-describedby={statusDescriptionId}
                  className={cn(
                    "gap-2 rounded-b-none rounded-t-md border px-4 py-2.5 data-[state=active]:border-b-white data-[state=active]:bg-white data-[state=active]:shadow-none",
                    familyTabStatusClasses[family.procurement_status ?? "not-set"],
                  )}
                >
                  <FileText className="size-4" aria-hidden="true" />
                  {family.name}
                  <span id={statusDescriptionId} className="sr-only">
                    Status: {familyTabStatusLabel(family.procurement_status)}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {orderedFamilies.map(family => (
          <TabsContent key={family.id} value={family.id} className="m-0">
            <div className="px-5 py-5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex size-10 flex-shrink-0 items-center justify-center rounded-md bg-[#f1e2ce] text-desert-sand-700">
                  <FileText className="size-5" aria-hidden="true" />
                </span>
                {/* Right padding mirrors the icon column so both margins match. */}
                <div className="min-w-0 flex-1 sm:pr-[calc(theme(spacing.10)+theme(spacing.3))]">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold text-gray-950">{family.name}</h3>
                    <Badge
                      variant={
                        family.procurement_status === "active"
                          ? "traffic-green"
                          : family.procurement_status === "expiring"
                            ? "traffic-amber"
                            : family.procurement_status === "expired" || family.procurement_status === "archived"
                              ? "traffic-red"
                              : "secondary"
                      }
                      className="capitalize"
                    >
                      {family.procurement_status ?? "Not set"}
                    </Badge>
                  </div>
                  <div
                    className="mt-2 flex flex-wrap items-center gap-2"
                    role="group"
                    aria-label={`${family.name} actions`}
                  >
                    <Badge variant="outline" className="bg-white font-medium text-gray-600">
                      {family.accessible_document_count} documents
                    </Badge>
                    <Button asChild type="button" size="sm" variant="outline">
                      <Link
                        to={`/drive/${encodeURIComponent(family.fs_entry_id ?? family.dms_fse_id ?? "")}?supplierContractFolderId=${encodeURIComponent(family.id)}&supplierId=${encodeURIComponent(supplierId)}`}
                      >
                        Documents <ExternalLink className="ml-1.5 size-3.5" aria-hidden="true" />
                      </Link>
                    </Button>
                    {family.contract_summary_outcome_id ? (
                      <Button asChild type="button" size="sm" variant="outline">
                        <Link to={`/reports/outcome/${encodeURIComponent(family.contract_summary_outcome_id)}`}>
                          Contract Summary <ExternalLink className="ml-1.5 size-3.5" aria-hidden="true" />
                        </Link>
                      </Button>
                    ) : null}
                    {(() => {
                      const badge = contractSummaryStatusBadge(family.contract_summary_status);
                      return (
                        <Badge variant={badge.variant} aria-label={`Contract summary: ${badge.label}`}>
                          {badge.label}
                        </Badge>
                      );
                    })()}
                    {hasContractFamilies && family.can_edit ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => setSummarising(family)}>
                        <Network className="mr-1.5 size-3.5" aria-hidden="true" />
                        {CONTRACT_SUMMARY_ACTION_LABELS[contractSummaryAction(family)]}
                      </Button>
                    ) : null}
                    {family.can_edit && supplierStatus === "active" ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => openEdit(family)}>
                        <Pencil className="mr-1.5 size-3.5" aria-hidden="true" /> Edit metadata
                      </Button>
                    ) : null}
                    {family.can_edit ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setDetaching(family);
                          setMessage(null);
                        }}
                      >
                        <Unlink className="mr-1.5 size-3.5" aria-hidden="true" /> Detach
                      </Button>
                    ) : null}
                    <Button type="button" size="sm" variant="outline" onClick={() => setAuditing(family)}>
                      <History className="mr-1.5 size-3.5" aria-hidden="true" /> Audit log
                    </Button>
                  </div>
                  {family.description || family.accessible_document_count > 0 ? (
                    <section aria-label={`${family.name} description`} className={cn("mt-4", FIELD_CARD_CLASSES)}>
                      {family.description ? (
                        <SummaryText text={family.description} className="text-sm text-gray-700" />
                      ) : (
                        <p className="text-sm text-gray-500">Description is being generated…</p>
                      )}
                    </section>
                  ) : null}
                  <dl className={cn("mt-4 text-sm text-gray-600", VALUE_GROUP_CLASSES)}>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">External ID</dt>
                      <dd>{family.external_id ?? "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Total value
                        <SummarySource show={fromSummary(family, "estimated_total_contract_value")} />
                      </dt>
                      <dd>
                        <ContractValue
                          value={family.estimated_total_contract_value}
                          currencyCode={family.currency_code}
                        />
                        <GbpEquivalent
                          value={family.estimated_total_contract_value_gbp}
                          currencyCode={family.currency_code}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Annual value
                        <SummarySource show={fromSummary(family, "estimated_annual_contract_value")} />
                      </dt>
                      <dd>
                        <ContractValue
                          value={family.estimated_annual_contract_value}
                          currencyCode={family.currency_code}
                        />
                        <GbpEquivalent
                          value={family.estimated_annual_contract_value_gbp}
                          currencyCode={family.currency_code}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Currency</dt>
                      <dd>{family.currency_code ?? "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Category</dt>
                      <dd>{family.category ?? "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Expiry date
                        <SummarySource show={fromSummary(family, "expiry_date")} />
                      </dt>
                      <dd>{family.expiry_date ? <SummaryDate date={family.expiry_date} /> : "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Divisions</dt>
                      <dd>{family.divisions?.length ? family.divisions.join(", ") : "Not set"}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium text-gray-500">Owner</dt>
                      <dd className="mt-1 space-y-1">
                        <FamilyOwnerDetails
                          owner={family.owner_user_id ? ownersById.get(family.owner_user_id) : undefined}
                        />
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium text-gray-500">Contact</dt>
                      <dd className="mt-1 space-y-1">
                        <span className="flex items-center gap-2 font-medium text-gray-700">
                          <UserRound className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                          {family.contact_name ?? "No contact name"}
                        </span>
                        <span className="flex items-center gap-2">
                          <Mail className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                          {family.contact_email ?? "No contact email"}
                        </span>
                        <span className="flex items-center gap-2">
                          <Phone className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                          {family.contact_phone ?? "No contact phone"}
                        </span>
                      </dd>
                    </div>
                  </dl>
                  <AiGeneratedValues family={family} />
                  <PriceEscalation family={family} />
                  <CommercialTerms family={family} />
                  <SupplierPerformance family={family} />
                </div>
              </div>
            </div>
            <SupplierFamilyNotes supplierId={supplierId} supplierStatus={supplierStatus} family={family} />
          </TabsContent>
        ))}
      </Tabs>

      {auditing ? (
        <SupplierFamilyAuditLogDialog supplierId={supplierId} family={auditing} onClose={() => setAuditing(null)} />
      ) : null}
      <Dialog open={Boolean(editing)} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contract Family metadata</DialogTitle>
            <DialogDescription>
              Update status, values, ownership, divisions and contact details in one place.
              {editing?.summary_fields?.length
                ? " Values found in the contract summary are shown only while the matching field here is empty."
                : null}
            </DialogDescription>
          </DialogHeader>
          {message ? (
            <p role="alert" className="text-sm text-red-700">
              {message}
            </p>
          ) : null}
          {editing ? (
            <ContractFamilyMetadataForm
              family={editing}
              supplierOwnerId={supplierOwnerId}
              ownerError={ownerError}
              isSubmitting={isUpdating}
              onCancel={() => setEditing(null)}
              onSubmit={saveMetadata}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Mounted on demand: the dialog reads the pipeline configuration as soon as it exists. */}
      {summarising ? (
        <ContractSummaryRegenerateDialog
          open
          onOpenChange={open => !open && setSummarising(null)}
          supplierContractFolderId={summarising.id}
          action={contractSummaryAction(summarising)}
        />
      ) : null}

      <Dialog open={Boolean(detaching)} onOpenChange={open => !open && setDetaching(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detach Contract Family</DialogTitle>
            <DialogDescription>
              Detach {detaching?.name} from this Supplier? Documents and summaries will not be deleted.
            </DialogDescription>
          </DialogHeader>
          {message ? (
            <p role="alert" className="text-sm text-red-700">
              {message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDetaching(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmDetach()} disabled={isDetaching}>
              {isDetaching ? "Detaching…" : "Detach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
